import React, { useState, useEffect, useContext } from 'react'
import PropTypes from 'prop-types'
import screenfull from 'screenfull'
import { navigate } from 'gatsby'

import { GameUnavailable } from '../../molecules/gameUnavailable'
import { CashbackProgress } from '../../molecules/cashbackProgress'
import { CapabilitiesDisclaimer } from '../../atoms/capabilitiesDisclaimer'
import { Clock } from '../../atoms/clock'
import { If } from '../../atoms/if'
import { Modal } from '../../atoms/modal'

import { AuthContext } from '../../../context/authProvider'
import useFeatures from '../../../hooks/useFeatures'
import useSlugs from '../../../hooks/useSlugs'

import TimesWhiteIcon from '../../../images/icon_times_white.svg'
import CompressIcon from '../../../images/icon_compress.svg'
import ExpandIcon from '../../../images/icon_expand.svg'

import 'react-circular-progressbar/dist/styles.css'

import {
  GameWithRGContent,
  HEADER_HEIGHT,
  ResponsibleGamingWrapper,
  StyledBtn,
  StyledCol,
  StyledGameActions,
  StyledGameContainer,
  StyledGameWrapper,
  StyledIframeCashbackWrapper,
  StyledIframeWrapper,
  StyledImgButton,
  StyledMobileHeaderButton,
  StyledMobileMenu,
  StyledTime,
} from './styles'

import useGameProviderUrl from '../../../hooks/useGameProviderUrl'
import { useTranslation } from '../../../context/translationProvider'
import useQueryParams from '../../../hooks/useQueryParams'
import { getCasinoBackLink, isBrowser } from '../../../utils/generic'
import { getPreviousPage } from '../../../utils/historyFunctions'
import {
  isiOS,
  sendMobileAppEvent,
} from '../../../utils/mobileAppFunctionality'
import useResizeListener from '../../../hooks/useResizeListener'
import {
  CASINO_PROVIDERS,
  FIFTEEN_SECONDS,
  TEN_SECONDS,
} from '../../../utils/constants'

const { ORYX, PLAYTECH } = CASINO_PROVIDERS
import { ResponsibleGaming } from '../../molecules/responsibleGaming'
import { useGameEventHandling } from '../../../hooks/useGameEventHandling'
import useScreenWidth from '../../../hooks/useScreenWidth'
import { pushDataLayer } from '../../../utils/pushDataLayer'
import useModal from '../../../hooks/useModal'
import { AutoWDConfirmationModal } from '../../molecules/autoWDConfirmationModal'
import { getAutoWithdrawalStatus } from '../../../adapters/user'
import { useGroowthbookLocaleFeature, useGrowthbookFeature } from '../../../hooks/useGrowthbook'

const renderEvoIframe = () => (
  <script
    dangerouslySetInnerHTML={{
      __html: `
        EvolutionGaming.init({
          iframeId: 'game'
        });
      `,
    }}
  />
)

const Game = (props) => {
  const {
    toggleCashbackDrawer,
    isUnderMaintenance,
    isBlockedByGeoLocation,
    gameEventData,
    isMobile: isMobileProp,
  } = props
  const { translate } = useTranslation()
  const params = useQueryParams()
  const { width } = useScreenWidth(0)
  const { openModal, closeModal, isOpen } = useModal()
  const {
    title,
    desktopSlug,
    mobileSlug,
    casino_provider,
    mock,
    slug,
    skin_id: skin,
    providerSlug,
    responsibleGaming,
    coming_soon,
  } = props.context

  const isMobile =
    casino_provider.toUpperCase() === PLAYTECH
      ? width < 1224 && width > 0
      : isMobileProp

  const DEFAULT_SCALED_GAME_PERCENT_HEIGHT = 85

  const {
    isLoggedIn,
    user,
    enableWalletSocketFallback,
    getUserWallet,
    getVaultData,
  } = useContext(AuthContext)
  const isAutoWDSettingEnabled = useGroowthbookLocaleFeature(
    'fe_igp_auto_withdrawal_setting_enabled'
  )
  const { userIds } = useGrowthbookFeature('fe_igp_override_game_unavailability', {userIds: []})
  const isTestUser = userIds?.includes(user?.id)

  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [mobileGameWrapper, setMobileGameWrapper] = useState({})
  const [casinoProvider, setCasinoProvider] = useState('')
  const [isiOSLandscape, setIsiOSLandscape] = useState(false)
  const [autoWDEnabled, setAutoWDEnabled] = useState()
  const [casinoSlug, liveCasinoSlug, depositSlug] = useSlugs([
    'casino',
    'live-casino',
    'deposit',
    'sports',
  ])
  const casinoBackLink = getCasinoBackLink(casinoSlug, liveCasinoSlug)

  if (isAutoWDSettingEnabled && autoWDEnabled) !isOpen && openModal()

  const isCasinoGameWalletPullingEnabled = useFeatures(
    'casinoGameWalletPulling'
  )

  useEffect(() => {
    const getAutoWDStatus = async () => {
      const { data } = await getAutoWithdrawalStatus()
      setAutoWDEnabled(data?.data?.optedIn)
    }
    getAutoWDStatus()
  }, [])

  useEffect(() => {
    setCasinoProvider(casino_provider?.toUpperCase())
  }, [casino_provider])

  const [placeholder, setPlaceholder] = useState({
    width: 0,
    height: 0,
    scaledW: 1366,
    scaledH: 768,
  })

  const isCashbackEnabled = useFeatures('cashback')

  const gameProviderUrl = useGameProviderUrl(
    casinoProvider,
    desktopSlug,
    mobileSlug,
    isLoggedIn,
    isMobile,
    casinoSlug,
    skin,
    slug,
    mock,
    params,
    depositSlug,
    user,
    providerSlug
  )

  const getContainerSize = () => {
    return {
      containerWidth: document.documentElement.clientWidth,
      containerHeight: document.documentElement.clientHeight - 60,
    }
  }
  const getUnavailabilityReason = () => {
    let reason = ''
    if (isUnderMaintenance && !isBlockedByGeoLocation) reason = 'maintenance'
    if (isUnderMaintenance && isBlockedByGeoLocation) reason = 'geo-blocked'
    if (isBlockedByGeoLocation) reason = 'geo-blocked'
    if (autoWDEnabled) reason = 'auto-withdrawal-enabled'
    if (coming_soon) reason = 'coming-soon'
    return reason
  }

  const getPlaceholderSize = (
    suggestedWidth = 1366,
    suggestedHeight = 768,
    containerWidth,
    containerHeight
  ) => {
    let result = {
      width: 0,
      height: 0,
      scaledW: 0,
      scaledH: 0,
    }

    if (isBrowser()) {
      if (suggestedWidth > 0 && suggestedHeight > 0) {
        const _width = containerHeight / (suggestedHeight / suggestedWidth)
        const _height = containerWidth / (suggestedWidth / suggestedHeight)

        if (_height < containerHeight) {
          result = Object.assign(result, {
            height: _height,
            width: containerWidth,
          })
        } else {
          result = Object.assign(result, {
            height: containerHeight,
            width: _width,
          })
        }

        result = Object.assign(result, {
          scaledW: (result.width * DEFAULT_SCALED_GAME_PERCENT_HEIGHT) / 100,
          scaledH: (result.height * DEFAULT_SCALED_GAME_PERCENT_HEIGHT) / 100,
        })
      }
      return result
    }
  }

  useEffect(() => {
    const setGameSize = () => {
      const { containerWidth, containerHeight } = getContainerSize()
      return getPlaceholderSize(1366, 768, containerWidth, containerHeight)
    }

    const placeholderSize = setGameSize()
    setPlaceholder(placeholderSize)

    let timeoutId = null
    const resizeListener = () => {
      clearTimeout(timeoutId)
      const placeholderSize = setGameSize()

      timeoutId = setTimeout(() => setPlaceholder(placeholderSize), 10)
    }

    window.addEventListener('resize', resizeListener)

    return () => {
      window.removeEventListener('resize', resizeListener)
    }
  }, [])

  useEffect(() => {
    if (!isMobile || !window?.screen | !isiOS()) return

    const onOrientationChange = () => {
      setTimeout(() => {
        const _isiOSLandscape =
          window?.screen?.orientation?.type?.startsWith('landscape-')
        setIsiOSLandscape(_isiOSLandscape)
      }, 100)
    }
    onOrientationChange()

    window.addEventListener('orientationchange', onOrientationChange)
    return () => {
      window.removeEventListener('orientationchange', onOrientationChange)
    }
  }, [])

  useResizeListener(() => {
    if (!isMobileProp) return
    setMobileGameWrapper({
      width: window?.innerWidth,
      height: window?.innerHeight - HEADER_HEIGHT,
    })
  }, [isMobileProp])

  useEffect(() => {
    setShowDisclaimer(!user?.user_capabilities?.play_casino)
  }, [user])

  useGameEventHandling(
    casinoProvider,
    providerSlug,
    isCasinoGameWalletPullingEnabled,
    depositSlug,
    casinoBackLink
  )

  useEffect(() => {
    if (
      !isLoggedIn ||
      !enableWalletSocketFallback ||
      !isCasinoGameWalletPullingEnabled
    )
      return

    const INTERVAL = casinoProvider === ORYX ? FIFTEEN_SECONDS : TEN_SECONDS

    const walletInterval = setInterval(() => {
      getUserWallet()
      getVaultData({ load: false })
    }, INTERVAL)

    return function () {
      clearInterval(walletInterval)
    }
  }, [
    isLoggedIn,
    enableWalletSocketFallback,
    isCasinoGameWalletPullingEnabled,
    casinoProvider,
  ])

  const onClose = (buttonType) => {
    if (typeof buttonType === 'string')
      pushDataLayer({
        eventName: buttonType,
        data: gameEventData,
      })
    sendMobileAppEvent('goBack')

    navigate(getPreviousPage())
  }

  const iframeStyle = {
    width: !isMobileProp ? `${placeholder.scaledW}px` : '100%',
    height: !isMobileProp ? `${placeholder.scaledH}px` : '100%',
    paddingBottom: isiOSLandscape ? '20px' : '0',
  }

  const handleToggleFullscreen = (action) => {
    if (action === 'expand') {
      pushDataLayer({
        eventName: 'game-expanded',
        data: gameEventData,
      })
    }
    screenfull.toggle()
  }

  const isGameNotAvailable = () => {
    if (autoWDEnabled) return true
    if (isTestUser) return false
    if (isUnderMaintenance) return true
    if (isBlockedByGeoLocation) return true
    if (coming_soon) return true
    return false
  }

  const notAvailable = isGameNotAvailable()
  const reason = getUnavailabilityReason()

  return (
    <div>
      <If
        condition={isMobileProp}
        render={() => (
          <StyledMobileMenu>
            <StyledMobileHeaderButton
              className="goBack"
              onClick={() => onClose('button_back')}
            />
            <p>{title}</p>
            <StyledMobileHeaderButton
              className="close"
              onClick={() => onClose('game-closed')}
            />
          </StyledMobileMenu>
        )}
      />
      <If
        condition={isOpen}
        render={() => (
          <Modal
            title={translate('account.limits.autoWD')}
            isOpen={isOpen}
            onClose={closeModal}
            theme={'form'}
            hideClose
          >
            <AutoWDConfirmationModal
              casinoBackLink={casinoBackLink}
              setAutoWDEnabled={setAutoWDEnabled}
              closeModal={closeModal}
            />
          </Modal>
        )}
      />
      <If
        condition={isLoggedIn && showDisclaimer}
        render={() => (
          <Modal
            theme="payments"
            title={translate('navigation.casino')}
            isOpen={true}
            onClose={onClose}
          >
            <CapabilitiesDisclaimer onClose={onClose} />
          </Modal>
        )}
        renderElse={() => (
          <StyledGameContainer>
            <StyledCol>
              <StyledGameWrapper>
                <StyledIframeCashbackWrapper>
                  <GameWithRGContent>
                    <If
                      condition={notAvailable}
                      render={() => (
                        <GameUnavailable
                          reason={reason}
                          casinoBackLink={casinoBackLink}
                          liveCasinoSlug={liveCasinoSlug}
                          iframeStyle={iframeStyle}
                        />
                      )}
                      renderElse={() => (
                        <StyledIframeWrapper style={{ ...mobileGameWrapper }}>
                          <iframe
                            allowfullscreen
                            id="game"
                            title={title}
                            style={iframeStyle}
                            src={gameProviderUrl}
                          />
                          <StyledTime>
                            <Clock hideWhenEmpty />
                          </StyledTime>
                        </StyledIframeWrapper>
                      )}
                    />
                    <If
                      condition={!isMobileProp}
                      render={() => (
                        <ResponsibleGamingWrapper>
                          <ResponsibleGaming
                            responsibleGaming={responsibleGaming}
                            addOver18Icon
                          />
                        </ResponsibleGamingWrapper>
                      )}
                    />
                  </GameWithRGContent>

                  <If
                    condition={!isMobileProp && user && isCashbackEnabled}
                    render={() => (
                      <CashbackProgress
                        toggleCashbackDrawer={toggleCashbackDrawer}
                        gameEventData={gameEventData}
                      />
                    )}
                  />
                </StyledIframeCashbackWrapper>
              </StyledGameWrapper>
              <If
                condition={!isMobileProp}
                render={() => (
                  <StyledGameActions>
                    <StyledBtn
                      type="button"
                      onClick={() => onClose('game-closed')}
                    >
                      <StyledImgButton src={TimesWhiteIcon} alt="Close game" />
                    </StyledBtn>
                    <StyledBtn type="button">
                      <If
                        condition={screenfull.isFullscreen}
                        render={() => (
                          <StyledImgButton
                            src={CompressIcon}
                            alt="Close game fullscreen"
                            onClick={() => handleToggleFullscreen()}
                          />
                        )}
                        renderElse={() => (
                          <StyledImgButton
                            src={ExpandIcon}
                            alt="Open game fullscreen"
                            onClick={() => handleToggleFullscreen('expand')}
                          />
                        )}
                      />
                    </StyledBtn>
                  </StyledGameActions>
                )}
              />

              <If
                condition={!isMobile && casinoProvider === 'EVOLUTION'}
                render={renderEvoIframe}
              />
            </StyledCol>
          </StyledGameContainer>
        )}
      />
    </div>
  )
}

Game.defaultProps = {
  isUnderMaintenance: false,
}

Game.propTypes = {
  context: PropTypes.shape({
    title: PropTypes.string,
    desktopSlug: PropTypes.string,
    mobileSlug: PropTypes.string,
    casino_provider: PropTypes.string,
    mock: PropTypes.bool,
    slug: PropTypes.string,
    skin_id: PropTypes.string,
    providerSlug: PropTypes.string,
  }).isRequired,
  toggleCashbackDrawer: PropTypes.func,
  isUnderMaintenance: PropTypes.bool,
}

export { Game }
